import { Card, CardContent, CardHeader, CardTitle } from '@ibsys/kit';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useTimeDisplayFormat, useUpdateGeneralSettings } from '@/entities/settings';
import type { TimeDisplayFormat, TimeDisplaySettings } from '@/entities/settings';
import { TimeDisplayFormatField } from './TimeDisplayFormatField';

type Props = {
  /** Изменение общих настроек доступно только с правом администрирования раздела */
  canEdit: boolean;
};

/** Настройки группы в порядке показа: сохраняемая настройка и ключ её названия */
const TIME_DISPLAY_FIELDS = [
  { name: 'playerTimeDisplayFormat', labelKey: 'timeDisplayFormat.player' },
  { name: 'transcriptTimeDisplayFormat', labelKey: 'timeDisplayFormat.transcript' },
  { name: 'expertReportTimeDisplayFormat', labelKey: 'timeDisplayFormat.expertReport' },
  { name: 'filterTimeDisplayFormat', labelKey: 'timeDisplayFormat.filter' },
  { name: 'timeDisplayFormat', labelKey: 'timeDisplayFormat.other' },
] as const satisfies { name: keyof TimeDisplaySettings; labelKey: string }[];

/**
 * Карточка раздела «Общее» с настройками отображения времени: у каждого компонента
 * своя настройка, остальные интервалы задаются отдельной.
 * Свои данные и их сохранение держит сама: страница только размещает карточку.
 */
export const TimeSettingsCard: FC<Props> = ({ canEdit }) => {
  const { t } = useTranslation('generalSettings');
  const formats: TimeDisplaySettings = {
    playerTimeDisplayFormat: useTimeDisplayFormat('player'),
    transcriptTimeDisplayFormat: useTimeDisplayFormat('transcript'),
    expertReportTimeDisplayFormat: useTimeDisplayFormat('expertReport'),
    filterTimeDisplayFormat: useTimeDisplayFormat('filter'),
    timeDisplayFormat: useTimeDisplayFormat(),
  };
  const { mutate: updateGeneralSettings, isPending } = useUpdateGeneralSettings();

  // Настройки независимы, поэтому уходит только изменённая: остальные сохраняют действующее значение
  const saveFormat = (name: keyof TimeDisplaySettings) => (value: TimeDisplayFormat) =>
    updateGeneralSettings({ [name]: value });

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('timeSection')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {TIME_DISPLAY_FIELDS.map(({ name, labelKey }) => (
            <TimeDisplayFormatField
              key={name}
              label={t(labelKey)}
              value={formats[name]}
              disabled={!canEdit || isPending}
              onChange={saveFormat(name)}
            />
          ))}
        </div>
        <p className="text-sm text-muted-foreground">{t('timeDisplayFormat.description')}</p>
      </CardContent>
    </Card>
  );
};
